import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

// Belt color mapping (hex colors for inline styles)
const BELT_HEX_COLORS: Record<string, string> = {
    'white': '#f5f5f5',
    'grey': '#9ca3af',
    'gray': '#9ca3af', // Alias
    'yellow': '#facc15',
    'orange': '#f97316',
    'green': '#22c55e',
    'blue': '#2563eb',
    'purple': '#9333ea',
    'brown': '#92400e',
    'black': '#1f2937',
};

// Fallback color for unknown belts
const DEFAULT_BELT_COLOR = '#d1d5db';

interface BeltAvatarProps {
    src?: string | null;
    fallback?: string;
    belt?: string | null;
    stripes?: number;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    className?: string;
    showBeltBorder?: boolean;
}

// Explicit pixel sizes for outer container
const SIZE_PX = {
    sm: 32,
    md: 40,
    lg: 48,
    xl: 64,
};

// Ring thickness (how much of the belt pattern is visible)
const RING_THICKNESS = {
    sm: 3,
    md: 4,
    lg: 4,
    xl: 5,
};

// Stripe indicator sizes (for degree stripes at bottom)
const STRIPE_PATCH_SIZE = {
    sm: { width: 10, height: 6, stripeWidth: 1.5 },
    md: { width: 12, height: 7, stripeWidth: 2 },
    lg: { width: 14, height: 8, stripeWidth: 2 },
    xl: { width: 18, height: 10, stripeWidth: 2.5 },
};

/**
 * Parse a belt name to extract base color and stripe color
 */
const parseBeltName = (belt: string): { base: string; stripe: string | null } => {
    const normalized = belt.toLowerCase().trim();
    const parts = normalized.split(/[\s-]+/);

    if (parts.length === 2) {
        return { base: parts[0], stripe: parts[1] };
    }

    return { base: normalized, stripe: null };
};


export const BeltAvatar: React.FC<BeltAvatarProps> = ({
    src,
    fallback,
    belt,
    stripes = 0,
    size = 'md',
    className,
    showBeltBorder = true,
}) => {
    // Parse belt name
    const { base: baseColorName, stripe: stripeColorName } = belt
        ? parseBeltName(belt)
        : { base: '', stripe: null };

    // Get hex colors
    const baseHex = BELT_HEX_COLORS[baseColorName] || DEFAULT_BELT_COLOR;
    const stripeHex = stripeColorName ? (BELT_HEX_COLORS[stripeColorName] || null) : null;

    // Check if valid belt
    const hasValidBelt = baseColorName && BELT_HEX_COLORS[baseColorName];

    // For white belt base, use a darker visible color
    const displayBaseColor = baseColorName === 'white' ? '#d4d4d4' : baseHex;

    // Stripe patch colors: black belt gets red patch, others get black
    const patchColor = baseColorName === 'black' ? '#dc2626' : '#1f2937';
    const stripeDisplayColor = '#ffffff';

    // Get sizes
    const patchDims = STRIPE_PATCH_SIZE[size];
    const outerSize = SIZE_PX[size];
    const ringThickness = RING_THICKNESS[size];
    const innerSize = outerSize - (ringThickness * 2);

    // Clamp stripes to 0-4
    const displayStripes = Math.min(Math.max(stripes || 0, 0), 4);

    // If no belt border or invalid belt, use simple styling
    if (!showBeltBorder || !hasValidBelt) {
        return (
            <Avatar
                className={cn('border-2 border-gray-200', className)}
                style={{ width: outerSize, height: outerSize }}
            >
                <AvatarImage src={src || undefined} />
                <AvatarFallback className="bg-gray-100 text-gray-600 font-medium">
                    {fallback}
                </AvatarFallback>
            </Avatar>
        );
    }

    // Determine if we need the fancy two-tone border
    const hasStripe = !!stripeHex;

    return (
        <div
            className={cn("relative inline-flex shrink-0 items-center justify-center", className)}
            style={{ width: outerSize, height: outerSize }}
        >
            {/* Belt ring - gradient for compound belts, solid for simple belts */}
            <div
                className="absolute inset-0 rounded-full"
                style={{
                    background: hasStripe
                        ? `linear-gradient(to right, ${displayBaseColor} 30%, ${stripeHex} 30%, ${stripeHex} 70%, ${displayBaseColor} 70%)`
                        : displayBaseColor,
                }}
            />

            {/* Inner avatar - smaller than outer to show belt ring */}
            <div
                className="relative z-10 rounded-full overflow-hidden"
                style={{
                    width: innerSize,
                    height: innerSize,
                    backgroundColor: '#fff', // White background to cover gradient behind
                }}
            >
                {src ? (
                    <img
                        src={src}
                        alt=""
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div
                        className="w-full h-full flex items-center justify-center font-medium"
                        style={{
                            backgroundColor: `${baseHex}30`,
                            color: baseColorName === 'white' || baseColorName === 'yellow' ? '#374151' : baseHex,
                            fontSize: innerSize * 0.4,
                        }}
                    >
                        {fallback}
                    </div>
                )}
            </div>

            {/* Stripe Patch - degree stripes at bottom */}
            {displayStripes > 0 && (
                <div
                    className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 rounded-sm flex items-center justify-center gap-px z-20"
                    style={{
                        backgroundColor: patchColor,
                        width: patchDims.width,
                        height: patchDims.height,
                        padding: '1px',
                    }}
                >
                    {Array.from({ length: displayStripes }).map((_, i) => (
                        <div
                            key={i}
                            style={{
                                width: patchDims.stripeWidth,
                                height: '100%',
                                backgroundColor: stripeDisplayColor,
                                borderRadius: 0.5,
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// Helper to get belt color for external use
export const getBeltColor = (belt: string | null | undefined): string => {
    if (!belt) return DEFAULT_BELT_COLOR;
    const { base } = parseBeltName(belt);
    return BELT_HEX_COLORS[base] || DEFAULT_BELT_COLOR;
};

